/* NBA Paris Game 2027 — interactions du thème */
(() => {
  const cfg = window.nbaConfig || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;
  const store = {
    get(area, key) { try { return window[area].getItem(key); } catch (e) { return null; } },
    set(area, key, value) { try { window[area].setItem(key, value); } catch (e) {} },
    remove(area, key) { try { window[area].removeItem(key); } catch (e) {} },
  };

  const formatMoney = window.nbaFormatMoney = (cents) => {
    const value = cents / 100;
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: cfg.currency || 'EUR',
        minimumFractionDigits: value % 1 ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      return value.toFixed(value % 1 ? 2 : 0) + ' €';
    }
  };

  /* ---------- Loader + révélations ---------- */
  let revealWatching = false;
  const startPage = () => {
    document.querySelectorAll('[data-nba-hero]').forEach((hero) => hero.classList.add('is-revealed'));
    initReveal();
    if (revealWatching || !('MutationObserver' in window)) return;
    revealWatching = true;
    // Sections ré-injectées (éditeur, rechargement) : on observe aussi les nouveaux éléments
    new MutationObserver((muts) => {
      const added = muts.some((m) => [...m.addedNodes].some((n) => n.nodeType === 1 && (n.matches('[data-nba-reveal]') || n.querySelector('[data-nba-reveal]'))));
      if (added) initReveal();
    }).observe(document.body, { childList: true, subtree: true });
  };

  const initLoader = () => {
    const loader = document.getElementById('NbaLoader');
    if (!loader) return startPage();
    const quick = loader.classList.contains('is-quick') || reduceMotion;
    const minTime = quick ? 100 : 1200;
    const t0 = performance.now();
    requestAnimationFrame(() => loader.classList.add('is-ready'));
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      const wait = Math.max(0, minTime - (performance.now() - t0));
      setTimeout(() => {
        loader.classList.add('is-done');
        store.set('sessionStorage', 'nbaLoaderSeen', '1');
        setTimeout(startPage, quick ? 150 : 450);
        setTimeout(() => loader.classList.add('is-gone'), 1300);
      }, wait);
    };
    if (document.readyState === 'complete') finish();
    else window.addEventListener('load', finish, { once: true });
    setTimeout(finish, 3500); // ne jamais bloquer l'utilisateur
  };

  const initReveal = () => {
    const items = document.querySelectorAll('[data-nba-reveal]:not(.is-in)');
    if (!('IntersectionObserver' in window)) return items.forEach((el) => el.classList.add('is-in'));
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* ---------- Header ---------- */
  const initHeader = () => {
    const header = document.querySelector('[data-nba-header]');
    if (!header) return;
    const setHeight = () => document.documentElement.style.setProperty('--nba-header-h', header.offsetHeight + 'px');
    setHeight();
    window.addEventListener('resize', setHeight);
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const burger = document.querySelector('[data-nba-burger]');
    const menu = document.querySelector('[data-nba-menu]');
    if (!burger || !menu) return;
    const focusables = () => menu.querySelectorAll('a');
    const toggle = (open) => {
      menu.classList.toggle('is-open', open);
      header.classList.toggle('is-menu-open', open);
      burger.setAttribute('aria-expanded', open);
      burger.setAttribute('aria-label', open ? 'Fermer le menu' : 'Ouvrir le menu');
      menu.setAttribute('aria-hidden', !open);
      document.body.classList.toggle('nba-lock', open);
      focusables().forEach((a) => a.setAttribute('tabindex', open ? '0' : '-1'));
    };
    burger.addEventListener('click', () => toggle(!menu.classList.contains('is-open')));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) toggle(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && menu.classList.contains('is-open')) { toggle(false); burger.focus(); } });
    window.addEventListener('resize', () => { if (window.innerWidth >= 1100 && menu.classList.contains('is-open')) toggle(false); });
  };

  const initAnnouncements = () => {
    document.querySelectorAll('[data-nba-announce]').forEach((bar) => {
      const msgs = [...bar.querySelectorAll('.nba-announce__msg')];
      if (msgs.length < 2 || reduceMotion) return;
      let i = 0;
      setInterval(() => {
        const prev = msgs[i];
        i = (i + 1) % msgs.length;
        prev.classList.remove('is-active');
        prev.classList.add('is-leaving');
        setTimeout(() => prev.classList.remove('is-leaving'), 600);
        msgs[i].classList.add('is-active');
      }, +bar.dataset.interval || 4000);
    });
  };

  /* ---------- Comptes à rebours ---------- */
  const initCountdowns = () => {
    const els = document.querySelectorAll('[data-nba-countdown]');
    if (!els.length) return;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      els.forEach((el) => {
        const target = Date.parse(el.dataset.date || cfg.eventDate);
        if (isNaN(target)) return;
        let diff = Math.max(0, target - Date.now());
        const d = Math.floor(diff / 864e5); diff -= d * 864e5;
        const h = Math.floor(diff / 36e5); diff -= h * 36e5;
        const m = Math.floor(diff / 6e4); diff -= m * 6e4;
        const s = Math.floor(diff / 1e3);
        const set = (unit, v) => { const n = el.querySelector(`[data-unit="${unit}"]`); if (n && n.textContent !== v) n.textContent = v; };
        if (el.dataset.nbaCountdown === 'compact') return set('days', String(d));
        if (target <= Date.now()) {
          el.innerHTML = '<span class="nba-countdown__live">C\'est le jour du match !</span>';
          return;
        }
        set('days', pad(d)); set('hours', pad(h)); set('minutes', pad(m)); set('seconds', pad(s));
      });
    };
    tick();
    setInterval(tick, 1000);
  };

  /* ---------- Effets ---------- */
  const initGlow = () => {
    document.querySelectorAll('[data-nba-glow]').forEach((card) => {
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${e.clientX - r.left}px`);
        card.style.setProperty('--my', `${e.clientY - r.top}px`);
      });
    });
  };

  const initParallax = () => {
    const els = [...document.querySelectorAll('[data-nba-parallax]')];
    if (!els.length || reduceMotion) return;
    let ticking = false;
    const update = () => {
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        const progress = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.setProperty('--parallax', `${(-10 + progress * 12).toFixed(2)}%`);
      });
      ticking = false;
    };
    update();
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  };

  const initAccordions = () => {
    document.querySelectorAll('[data-nba-accordion] details').forEach((details) => {
      const summary = details.querySelector('summary');
      const body = details.querySelector('.nba-accordion__body');
      if (!summary || !body || reduceMotion) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (details.dataset.animating) return;
        details.dataset.animating = '1';
        const opening = !details.open;
        if (opening) details.open = true;
        const full = body.scrollHeight;
        const anim = body.animate(
          { height: opening ? ['0px', `${full}px`] : [`${full}px`, '0px'], opacity: opening ? [0, 1] : [1, 0] },
          { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        );
        anim.onfinish = () => {
          if (!opening) details.open = false;
          delete details.dataset.animating;
        };
      });
    });
  };

  const initTabs = () => {
    document.querySelectorAll('[data-nba-tabs]').forEach((wrap) => {
      const tabs = [...wrap.querySelectorAll('[role="tab"]')];
      const select = (tab) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on);
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
          select(next);
          next.focus();
        });
      });
    });
  };

  /* ---------- Ajout à l'agenda ---------- */
  const initCalendar = () => {
    const start = new Date(cfg.eventDate);
    if (isNaN(start)) return;
    const end = new Date(start.getTime() + 150 * 60000);
    const stamp = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const title = cfg.eventTitle || 'NBA Paris Game 2027';
    const location = cfg.eventLocation || '';
    document.querySelectorAll('[data-nba-gcal]').forEach((a) => {
      a.href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${stamp(start)}/${stamp(end)}&location=${encodeURIComponent(location)}`;
    });
    document.querySelectorAll('[data-nba-ics]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const ics = [
          'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//NBA Paris Game//FR', 'BEGIN:VEVENT',
          `UID:nba-paris-game-2027@${location.length}`, `DTSTAMP:${stamp(new Date())}`,
          `DTSTART:${stamp(start)}`, `DTEND:${stamp(end)}`, `SUMMARY:${title}`, `LOCATION:${location.replace(/,/g, '\\,')}`,
          'BEGIN:VALARM', 'TRIGGER:-PT3H', 'ACTION:DISPLAY', `DESCRIPTION:${title}`, 'END:VALARM',
          'END:VEVENT', 'END:VCALENDAR',
        ].join('\r\n');
        const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
        const link = Object.assign(document.createElement('a'), { href: url, download: 'nba-paris-game-2027.ics' });
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
    });
  };

  /* ---------- Réservation du panier ---------- */
  const HOLD_KEY = 'nbaHoldUntil';
  const initHold = () => {
    const minutes = +cfg.holdMinutes || 15;
    if (cfg.cartCount === 0) store.remove('localStorage', HOLD_KEY);
    const start = () => {
      const until = +store.get('localStorage', HOLD_KEY);
      if (!until || until < Date.now()) store.set('localStorage', HOLD_KEY, String(Date.now() + minutes * 60000));
    };
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const count = event && event.cartData && event.cartData.item_count;
        if (count === 0) store.remove('localStorage', HOLD_KEY);
        else if (event && event.source === 'product-form') start();
      });
    }
    if (cfg.cartCount > 0 && !store.get('localStorage', HOLD_KEY)) start();
    document.addEventListener('nba:cart-added', start);
    const tick = () => {
      const until = +store.get('localStorage', HOLD_KEY);
      document.querySelectorAll('[data-nba-hold]').forEach((el) => {
        if (!until) { el.hidden = true; return; }
        el.hidden = false;
        const left = until - Date.now();
        const bar = el.querySelector('[data-nba-hold-bar]');
        const t = el.querySelector('[data-nba-hold-time]');
        if (left <= 0) {
          el.classList.add('is-expired');
          el.querySelector('[data-nba-hold-text]').innerHTML = '<b>Délai écoulé</b><small>Vos places restent dans le panier sous réserve de disponibilité.</small>';
          if (t) t.textContent = '0:00';
          if (bar) bar.style.transform = 'scaleX(0)';
          return;
        }
        el.classList.toggle('is-urgent', left < 120000);
        const m = Math.floor(left / 60000);
        const s = Math.floor((left % 60000) / 1000);
        if (t) t.textContent = `${m}:${String(s).padStart(2, '0')}`;
        if (bar) bar.style.transform = `scaleX(${Math.min(1, left / (minutes * 60000)).toFixed(4)})`;
      });
    };
    tick();
    setInterval(tick, 1000);
  };

  const init = () => {
    initLoader();
    initHeader();
    initAnnouncements();
    initCountdowns();
    initGlow();
    initParallax();
    initAccordions();
    initTabs();
    initCalendar();
    initHold();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => {
    initReveal();
    initCountdowns();
    document.querySelectorAll('[data-nba-hero]').forEach((hero) => hero.classList.add('is-revealed'));
  });
})();
